import { useMemo, useRef } from 'react'

import { ProcurementWorkflowEntitiesByIdsDocument } from 'generated/resources/graphql'
import isEqual from 'lodash/isEqual'

import { useQuery } from 'modules/core/hooks/useQuery'

import { EntityResolution } from 'modules/workflows/contexts/WorkflowContext'

/**
 * Hook for resolving workflow entity IDs to display names.
 * This is procurement-specific - it knows about team and role entities.
 *
 * Returns a stable reference when the actual data hasn't changed,
 * preventing unnecessary re-renders.
 *
 * @param entityIds - Record mapping entity type strings to arrays of IDs
 * @returns EntityResolution map with resolved names
 */
export function useWorkflowEntityResolution(entityIds: Record<string, string[]>): EntityResolution {
  // Keep track of the previous resolution to maintain stable reference
  const prevResolutionRef = useRef<EntityResolution>({ team: {}, role: {} })

  // Check if we have any IDs to fetch
  const hasEntityIds = (entityIds.team?.length ?? 0) > 0 || (entityIds.role?.length ?? 0) > 0

  // Fetch all entities in a single query
  const { data: entitiesData } = useQuery(ProcurementWorkflowEntitiesByIdsDocument, {
    variables: {
      teamIds: (entityIds.team ?? []).map(Number),
      roleIds: (entityIds.role ?? []).map(Number),
    },
    skip: !hasEntityIds,
  })

  // Build entity resolution map with stable reference
  return useMemo(() => {
    const teamMap: Record<string, string> = {}
    const roleMap: Record<string, string> = {}

    for (const team of entitiesData?.teams?.teamsConnection?.nodes ?? []) {
      teamMap[team.id] = team.name
    }

    for (const role of entitiesData?.jobCatalog?.rolesConnection?.nodes ?? []) {
      roleMap[role.id] = role.name
    }

    const newResolution = { team: teamMap, role: roleMap }

    // Return previous reference if data hasn't actually changed
    if (isEqual(prevResolutionRef.current, newResolution)) {
      return prevResolutionRef.current
    }

    prevResolutionRef.current = newResolution
    return newResolution
  }, [entitiesData])
}
